import axios, { AxiosInstance } from 'axios';
import {
  AdminProfile,
  AuthResponse,
  DashboardSummary,
  UsersResponse,
  UserStats,
  AnalysesResponse,
  AnalysisStats,
  AccountsResponse,
  AccountStats,
  AccountDetails,
  TokenUsageStats,
  TokenUsageByAccountResponse,
  TokenUsageByLLMResponse,
  AnalysisTokenUsageResponse,
  AnalysisTelemetry,
  PaginationParams,
  CancelAnalysisResponse,
  SetAnalysisStatusRequest,
  SetAnalysisStatusResponse,
  RequeueAnalysisResponse,
  AccountManagementResponse,
  AddBalanceRequest,
  AddBalanceResponse,
  AccountBalance,
  TransactionsResponse,
  TransactionsParams,
  AccountPlanResponse,
  PlansResponse,
  SetPlanRequest,
  SetPlanResponse,
  FailedEventsResponse,
  FailedEventsStats,
  FailedEvent,
  RetryEventResponse,
  DeleteEventResponse,
  AccountFeaturesResponse,
  SetFeatureResponse,
  LLMPricingResponse,
  GlobalNotification,
  CreateNotificationRequest,
  UpdateNotificationRequest,
  NotificationsResponse,
  UsageTopUpsResponse,
  GrantUsageTopUpRequest,
  GrantUsageTopUpResponse,
  RevokeUsageTopUpResponse,
} from '../types/admin';

const runtimeConfig = (window as any).__RUNTIME_CONFIG__ || {};
const apiUrl = runtimeConfig.API_URL || 'http://localhost:8080/mngt';

class AdminApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: apiUrl,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          const currentPath = window.location.pathname;
          if (currentPath !== '/admin/login' && currentPath !== '/admin/auth/google/callback') {
            window.location.href = '/admin/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async getGoogleLoginUrl(): Promise<{ auth_url: string; state: string }> {
    const response = await this.client.get('/auth/google/login');
    return response.data;
  }

  async googleCallback(code: string, state: string): Promise<AuthResponse> {
    const response = await this.client.post('/auth/google/callback', { code, state });
    return response.data;
  }

  async logout(): Promise<{ message: string }> {
    const response = await this.client.post('/auth/logout');
    return response.data;
  }

  async getProfile(): Promise<AdminProfile> {
    const response = await this.client.get('/auth/profile');
    return response.data;
  }

  // Dashboard endpoints
  async getDashboard(): Promise<DashboardSummary> {
    const response = await this.client.get('/dashboard');
    return response.data;
  }

  // User endpoints
  async getUsers(params?: PaginationParams): Promise<UsersResponse> {
    const response = await this.client.get('/users', { params });
    return response.data;
  }

  async getUserStats(): Promise<UserStats> {
    const response = await this.client.get('/users/stats');
    return response.data;
  }

  // Analysis endpoints
  async getAnalyses(params?: PaginationParams): Promise<AnalysesResponse> {
    const response = await this.client.get('/analyses', { params });
    return response.data;
  }

  async getAnalysisStats(): Promise<AnalysisStats> {
    const response = await this.client.get('/analyses/stats');
    return response.data;
  }

  // Account endpoints
  async getAccounts(params?: PaginationParams): Promise<AccountsResponse> {
    const response = await this.client.get('/accounts', { params });
    return response.data;
  }

  async getAccountStats(): Promise<AccountStats> {
    const response = await this.client.get('/accounts/stats');
    return response.data;
  }

  async getAccountDetails(accountId: string): Promise<AccountDetails> {
    const response = await this.client.get(`/accounts/${accountId}`);
    return response.data;
  }

  // Token Usage endpoints
  async getTokenUsageSummary(): Promise<TokenUsageStats> {
    const response = await this.client.get('/token-usage/summary');
    return response.data;
  }

  async getTokenUsageByAccount(params?: PaginationParams): Promise<TokenUsageByAccountResponse> {
    const response = await this.client.get('/token-usage/by-account', { params });
    return response.data;
  }

  async getTokenUsageByLLM(): Promise<TokenUsageByLLMResponse> {
    const response = await this.client.get('/token-usage/by-llm');
    return response.data;
  }

  async getAnalysisTokenUsage(analysisId: string): Promise<AnalysisTokenUsageResponse> {
    const response = await this.client.get(`/token-usage/analysis/${analysisId}`);
    return response.data;
  }

  async getAnalysisTelemetry(analysisId: string): Promise<AnalysisTelemetry> {
    const response = await this.client.get(`/telemetry/${analysisId}`);
    return response.data;
  }

  // Admin Management endpoints

  // Cancel an analysis
  async cancelAnalysis(analysisId: string): Promise<CancelAnalysisResponse> {
    const response = await this.client.post(`/analyses/${analysisId}/cancel`);
    return response.data;
  }

  // Set analysis status
  async setAnalysisStatus(analysisId: string, data: SetAnalysisStatusRequest): Promise<SetAnalysisStatusResponse> {
    const response = await this.client.post(`/analyses/${analysisId}/status`, data);
    return response.data;
  }

  // Requeue analysis (reset to pending for re-processing)
  async requeueAnalysis(analysisId: string): Promise<RequeueAnalysisResponse> {
    const response = await this.client.post(`/analyses/${analysisId}/requeue`);
    return response.data;
  }

  // Set account to Pro
  async setAccountPro(accountId: string): Promise<AccountManagementResponse> {
    const response = await this.client.post(`/accounts/${accountId}/pro`);
    return response.data;
  }

  // Disable account
  async disableAccount(accountId: string): Promise<AccountManagementResponse> {
    const response = await this.client.post(`/accounts/${accountId}/disable`);
    return response.data;
  }

  // Enable account
  async enableAccount(accountId: string): Promise<AccountManagementResponse> {
    const response = await this.client.post(`/accounts/${accountId}/enable`);
    return response.data;
  }

  // Activate account
  async activateAccount(accountId: string): Promise<AccountManagementResponse> {
    const response = await this.client.post(`/accounts/${accountId}/activate`);
    return response.data;
  }

  // Add balance to account
  async addAccountBalance(accountId: string, data: AddBalanceRequest): Promise<AddBalanceResponse> {
    const response = await this.client.post(`/accounts/${accountId}/balance`, data);
    return response.data;
  }

  // Get account balance
  async getAccountBalance(accountId: string): Promise<AccountBalance> {
    const response = await this.client.get(`/accounts/${accountId}/balance`);
    return response.data;
  }

  // Get billing transactions
  async getTransactions(params?: TransactionsParams): Promise<TransactionsResponse> {
    const response = await this.client.get('/billing/transactions', { params });
    return response.data;
  }

  // Get account plan and usage
  async getAccountPlan(accountId: string): Promise<AccountPlanResponse> {
    const response = await this.client.get(`/accounts/${accountId}/plan`);
    return response.data;
  }

  // Every plan this build of the API will accept, so the picker cannot offer a
  // slug the server rejects or miss one it would take.
  async getPlans(): Promise<PlansResponse> {
    const response = await this.client.get('/plans');
    return response.data;
  }

  // Set account plan
  async setAccountPlan(accountId: string, data: SetPlanRequest): Promise<SetPlanResponse> {
    const response = await this.client.post(`/accounts/${accountId}/plan`, data);
    return response.data;
  }

  // Failed Events endpoints
  async getFailedEvents(params?: PaginationParams): Promise<FailedEventsResponse> {
    const response = await this.client.get('/failed-events', { params });
    return response.data;
  }

  async getFailedEventsStats(): Promise<FailedEventsStats> {
    const response = await this.client.get('/failed-events/stats');
    return response.data;
  }

  async getFailedEventsByAnalysis(analysisId: string): Promise<FailedEventsResponse> {
    const response = await this.client.get(`/failed-events/analysis/${analysisId}`);
    return response.data;
  }

  async getFailedEvent(eventId: string): Promise<FailedEvent> {
    const response = await this.client.get(`/failed-events/${eventId}`);
    return response.data;
  }

  async retryFailedEvent(eventId: string): Promise<RetryEventResponse> {
    const response = await this.client.post(`/failed-events/${eventId}/retry`);
    return response.data;
  }

  async deleteFailedEvent(eventId: string): Promise<DeleteEventResponse> {
    const response = await this.client.delete(`/failed-events/${eventId}`);
    return response.data;
  }

  async deleteFailedEventsByAnalysis(analysisId: string): Promise<DeleteEventResponse> {
    const response = await this.client.delete(`/failed-events/analysis/${analysisId}`);
    return response.data;
  }

  // Feature Flags
  async getAccountFeatures(accountId: string): Promise<AccountFeaturesResponse> {
    const response = await this.client.get(`/features/${accountId}`);
    return response.data;
  }

  async setAccountFeature(accountId: string, feature: string, enabled: boolean): Promise<SetFeatureResponse> {
    const response = await this.client.put(`/features/${accountId}`, { feature, enabled });
    return response.data;
  }

  // LLM Pricing
  async getPricing(): Promise<LLMPricingResponse> {
    const response = await this.client.get('/pricing');
    return response.data;
  }

  // Usage Top-ups
  async getUsageTopUps(accountId: string): Promise<UsageTopUpsResponse> {
    const response = await this.client.get(`/accounts/${accountId}/usage-top-ups`);
    return response.data;
  }

  async grantUsageTopUp(accountId: string, data: GrantUsageTopUpRequest): Promise<GrantUsageTopUpResponse> {
    const response = await this.client.post(`/accounts/${accountId}/usage-top-ups`, data);
    return response.data;
  }

  async revokeUsageTopUp(accountId: string, topUpId: string): Promise<RevokeUsageTopUpResponse> {
    const response = await this.client.delete(`/accounts/${accountId}/usage-top-ups/${topUpId}`);
    return response.data;
  }

  // Global Notifications
  async getNotifications(params?: PaginationParams): Promise<NotificationsResponse> {
    const response = await this.client.get('/notifications', { params });
    return response.data;
  }

  async createNotification(data: CreateNotificationRequest): Promise<GlobalNotification> {
    const response = await this.client.post('/notifications', data);
    return response.data;
  }

  async updateNotification(id: string, data: UpdateNotificationRequest): Promise<GlobalNotification> {
    const response = await this.client.put(`/notifications/${id}`, data);
    return response.data;
  }

  async deleteNotification(id: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/notifications/${id}`);
    return response.data;
  }
}

export const adminApiClient = new AdminApiClient();
